"use client";

import { Badge } from "@/components/shadcn/badge";
import { Card, CardContent } from "@/components/shadcn/card";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from "@/components/shadcn/carousel";
import { Link } from "@/i18n/navigation";
import { safeUrl } from "@/lib/utils";
import Autoplay from "embla-carousel-autoplay";
import Image from "next/image";

export type PartnerAd = {
    Id: number;
    ImageUrl: string;
    Text: string;
    Url: string;
};

const AUTOPLAY_DELAY_MS = 5000;

function PartnerCard({ ad }: { ad: PartnerAd }) {
    const href = safeUrl(ad.Url) ?? "#!";
    const imageUrl = safeUrl(ad.ImageUrl) ?? "/images/placeholder.jpg";

    return (
        <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ad.Text}
            className="block h-full"
        >
            <Card className="hover:border-primary/40 h-full transition-colors">
                <CardContent className="flex h-full flex-col items-center justify-between gap-3">
                    <div className="relative h-14 w-full">
                        <Image
                            src={imageUrl}
                            alt={ad.Text}
                            fill
                            sizes="(min-width: 640px) 20vw, 40vw"
                            className="object-contain"
                        />
                    </div>
                    <p className="text-muted-foreground line-clamp-2 text-center font-medium">
                        {ad.Text}
                    </p>
                    <Badge variant="secondary" className="uppercase">
                        Reklam
                    </Badge>
                </CardContent>
            </Card>
        </Link>
    );
}

export default function PartnersCarousel({ ads }: { ads: PartnerAd[] }) {
    return (
        <Carousel
            // Ads rotate on their own: no arrows, no dots, and `watchDrag: false`
            // so the widget never steals a swipe from the page scroll.
            opts={{ align: "start", loop: true, watchDrag: false }}
            plugins={[
                Autoplay({
                    delay: AUTOPLAY_DELAY_MS,
                    stopOnInteraction: false,
                    stopOnMouseEnter: false,
                }),
            ]}
            className="w-full"
        >
            <CarouselContent>
                {ads.map((ad) => (
                    <CarouselItem key={ad.Id} className="sm:basis-1/2">
                        <PartnerCard ad={ad} />
                    </CarouselItem>
                ))}
            </CarouselContent>
        </Carousel>
    );
}
